/**
 * @fileoverview Canonical event signature extractor for inspection events.
 * @module core-domain/inspection/eventSignatures
 *
 * Responsibilities:
 * - Extract signature data from form responses and route to event.signatures[].
 * - Enforce per-event (not per-response) signature semantics (plan §217-230).
 *
 * Dependencies: core-domain response types.
 * Architecture: Pure domain logic — no Dexie or API dependencies.
 * Notes: Signature widgets on forms are capture placeholders; values route to event.signatures[].
 */
// Signatures are per-inspection-event, NOT per-response (plan §217-230).
// Signature widgets on forms are capture placeholders whose values must
// be routed to `event.signatures[]`, not into `case.responses{}`.
//
// This module is a pure adapter. Given the template forms and a nested
// response map keyed by formId → fieldId → value, it produces an
// `EventSignature[]` destined for an event's `signatures[]` array.
//
// Both tenant-web and mobile-pwa consume this to converge on identical
// role-inference rules and identical capture semantics.
// ---------------------------------------------------------------------------

import type { AttachmentRefLike, EventSignature } from './responseTypes';

// Minimal structural field shape — accepts any object with `id`/`fieldId`,
// `type`, `label`, and optional `extras.signatureRole`. Callers do not have
// to import their app's concrete FormField union.
export interface SignatureExtractorField {
  id?: string;
  fieldId?: string;
  type?: string;
  label?: string;
  extras?: { signatureRole?: string };
}

export interface SignatureExtractorForm {
  _id: string;
  fields?: SignatureExtractorField[];
}

export interface ExtractSignaturesOpts {
  capturedAt: string;
  capturedBy: string;
  eventIndex: number;
}

export type SignatureRole = EventSignature['role'];

// ---------------------------------------------------------------------------
// Type guard — distinguishes an AttachmentRef payload (SignaturePad output)
// from the many other shapes a field value might hold.
// ---------------------------------------------------------------------------
export function isAttachmentRefValue(v: unknown): v is AttachmentRefLike {
  return !!v && typeof v === 'object' && typeof (v as { _id?: unknown })._id === 'string';
}

// ---------------------------------------------------------------------------
// `signature` is the canonical widget-type marker for all apps.
// ---------------------------------------------------------------------------
export function isSignatureField(field: SignatureExtractorField): boolean {
  return field.type === 'signature';
}

// ---------------------------------------------------------------------------
// Role inference:
//   1. `field.extras.signatureRole` — authoritative when present.
//   2. Fallback: label-heuristic ("Inspector", "Witness", "Owner").
//   3. Otherwise: 'other', with `roleLabel` = field.label so the audit
//      trail still tells a human which signature slot this was.
// ---------------------------------------------------------------------------
export function inferSignatureRole(
  field: SignatureExtractorField,
): { role: SignatureRole; roleLabel?: string } {
  const extrasRole = field.extras?.signatureRole;
  if (
    extrasRole === 'inspector' ||
    extrasRole === 'business' ||
    extrasRole === 'witness' ||
    extrasRole === 'other'
  ) {
    return { role: extrasRole, roleLabel: extrasRole === 'other' ? field.label : undefined };
  }
  const label = String(field.label ?? '').toLowerCase();
  if (label.includes('inspector')) return { role: 'inspector' };
  if (label.includes('witness')) return { role: 'witness' };
  if (label.includes('business') || label.includes('owner') || label.includes('responsible')) {
    return { role: 'business' };
  }
  return { role: 'other', roleLabel: field.label };
}

// ---------------------------------------------------------------------------
// Walk every signature field in every form and produce an EventSignature
// for each one holding a live AttachmentRef. Deterministic ordering:
// forms in the order passed; fields in the order declared on each form.
// ---------------------------------------------------------------------------
export function extractEventSignaturesFromResponses(
  forms: SignatureExtractorForm[],
  nestedResponses: Record<string, Record<string, unknown>>,
  opts: ExtractSignaturesOpts,
): EventSignature[] {
  const signatures: EventSignature[] = [];
  for (const form of forms) {
    const formResponses = nestedResponses[form._id] ?? {};
    const fieldList: SignatureExtractorField[] = Array.isArray(form.fields) ? form.fields : [];
    for (const field of fieldList) {
      if (!isSignatureField(field)) continue;
      const fieldId = field.id ?? field.fieldId;
      if (!fieldId || !(fieldId in formResponses)) continue;
      const value = formResponses[fieldId];
      if (!isAttachmentRefValue(value)) continue;
      const { role, roleLabel } = inferSignatureRole(field);
      // `capturedBy` is the acting user (the inspector operating the device). It is a valid
      // signer name ONLY for the inspector's own signature. For non-inspector roles, look for
      // a companion text field whose `extras.signerNameFor` points to this signature field
      // (G-009). If no companion field exists, signerName stays undefined so the report shows
      // a blank printed-name line rather than the wrong name.
      let signerName: string | undefined;
      if (role === 'inspector') {
        signerName = opts.capturedBy;
      } else {
        // G-009: Scan for a companion name field whose extras.signerNameFor === this fieldId
        const companionField = fieldList.find(
          (f) => f.extras && (f.extras as Record<string, unknown>).signerNameFor === fieldId,
        );
        if (companionField) {
          const companionId = companionField.id ?? companionField.fieldId;
          if (companionId) {
            const nameValue = formResponses[companionId];
            if (typeof nameValue === 'string' && nameValue.trim()) {
              signerName = nameValue.trim();
            } else if (nameValue && typeof nameValue === 'object' && 'value' in nameValue) {
              const v = (nameValue as { value?: unknown }).value;
              if (typeof v === 'string' && v.trim()) signerName = v.trim();
            }
          }
        }
      }
      signatures.push({
        _id: value._id,
        role,
        roleLabel,
        signerName,
        attachmentRef: value,
        capturedAt: opts.capturedAt,
        capturedBy: opts.capturedBy,
        capturedInEventIndex: opts.eventIndex,
      });
    }
  }
  return signatures;
}
